import React, { useRef, useState } from 'react';
import { IconPhotoUp, IconCamera } from '@tabler/icons-react';
import styles from './UploadFallback.module.scss';

interface UploadFallbackProps {
    // Called with a chosen/dropped image file.
    onFile: (file: File) => void;
    // Optional explanation for why the live viewfinder isn't available.
    note?: string;
    // When true, also offer the device's native camera (mobile without a
    // usable in-app viewfinder).
    offerNativeCamera?: boolean;
}

const UploadFallback = ({ onFile, note, offerNativeCamera }: UploadFallbackProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const handleFiles = (files: FileList | null) => {
        const file = files?.[0];
        if (file && file.type.startsWith('image/')) {
            onFile(file);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div
                className={`${styles.dropzone} ${dragging ? styles.dragging : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
            >
                <IconPhotoUp className={styles.dropIcon} />
                <span className={styles.dropTitle}>Drag &amp; drop a photo</span>
                <span className={styles.dropHint}>or click to browse</span>
                {note && <span className={styles.note}>{note}</span>}
            </div>

            {offerNativeCamera && (
                <button
                    type="button"
                    className={styles.cameraBtn}
                    onClick={() => cameraRef.current?.click()}
                >
                    <IconCamera size={20} />
                    <span>Use device camera</span>
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />
        </div>
    );
};

export default UploadFallback;
